/**
 * The country page narrowed to one CAMPAIGN GROUP: every group the switcher
 * offers (pins shared with the SMSWiser page), the open one, its campaigns
 * and container ids, the day plan of just those containers, and why Add
 * container is off (no group open, or a group without a master config).
 * For Media Devoted | Created By Ciro Autiero
 */
import { useMemo } from 'react';

import {
  type GroupEntry,
  groupEntries,
  scopeContainers,
  scopePreview,
} from '@/features/drop-generator/components/campaign/campaign-scope';
import { useCampaignGroups } from '@/features/drop-generator/components/campaign/use-campaign-groups';
import { useCampaignPins } from '@/features/drop-generator/components/campaign/use-campaign-pins';
import type { DayPlanPreview, DropGenPlanDetail } from '@/features/drop-generator/drop-generator.types';

/** A new container lives in ONE group and follows its master config. */
function addBlockedReason(current: GroupEntry | null): string | null {
  if (!current) return 'Open a campaign group from the switcher first — a new container runs on one of its campaigns and follows its master config.';
  return current.master ? null : 'This campaign group has no master config yet — create it first; every new container follows one.';
}

const EMPTY = { containers: [], masters: [] } as Pick<DropGenPlanDetail, 'containers' | 'masters'>;

export function useCampaignScope(
  plan: DropGenPlanDetail | undefined,
  countryKey: string,
  pick: { group: string | null; campaign: number | null },
  preview: DayPlanPreview | undefined,
) {
  const ix = useCampaignGroups(countryKey);
  const pins = useCampaignPins(countryKey);
  const source = plan ?? EMPTY;
  const entries = useMemo(() => groupEntries(source, ix, pins.pinned), [source, ix, pins.pinned]);
  const key = pick.group ?? (pick.campaign !== null ? ix.keyOf(pick.campaign) : null);
  const current: GroupEntry | null = key === null ? null : (entries.find((e) => e.key === key) ?? null);
  const memberIds = useMemo(() => (current ? new Set(current.members.map((m) => m.wiserId)) : null), [current]);
  const ids = useMemo(
    () => (memberIds ? new Set(scopeContainers(source.containers, memberIds).map((c) => c.id)) : null),
    [source.containers, memberIds],
  );
  return {
    ix,
    entries,
    current,
    memberIds,
    ids,
    pins,
    preview: scopePreview(preview, ids),
    addBlocked: addBlockedReason(current),
  };
}
